/**
 * Chat Service — API calls and SSE streaming for AI Chat.
 */
import apiClient, { getAuthHeaders } from './apiClient';
import { API_BASE_URL } from '../constants/config';

export interface Conversation {
  ConversationKey: string;
  Title: string | null;
  Model: string;
  MessageCount: number;
  TotalInputTokens: number;
  TotalOutputTokens: number;
  Status: string;
  LastMessageAt: string | null;
  sysCreateDate: string;
}

export interface ChatMessage {
  MessageKey: string;
  ConversationKey: string;
  Role: 'user' | 'assistant' | 'tool_result';
  Content: string | null;
  ToolName: string | null;
  ToolInput: string | null;
  ToolOutput: string | null;
  InputTokens: number;
  OutputTokens: number;
  DurationMs: number | null;
  CreatedAt: string;
}

export interface ChatSettings {
  SettingsKey: string;
  SystemPrompt: string;
  EnabledTools: string;
  Model: string;
  MaxTokensPerMessage: number;
  /** Reasoning effort for Claude Sonnet 5 models: low | medium | high | xhigh | max. */
  Effort: string;
}

export interface SSEEvent {
  type: 'text' | 'tool_start' | 'tool_executing' | 'tool_result' | 'thinking'
    | 'thinking_block_start' | 'thinking_text' | 'thinking_block_stop'
    | 'agent_stats' | 'done' | 'title' | 'error';
  text?: string;
  toolName?: string;
  input?: Record<string, unknown>;
  result?: string;
  status?: string;
  iteration?: number;
  inputTokens?: number;
  outputTokens?: number;
  durationMs?: number;
  title?: string;
  message?: string;
}

export interface SuggestedQuestion {
  text: string;
  icon: string;
  category: string;
  /** New MoftakiAI capability (v1.0.43) — showcase UIs may badge these. */
  isNew?: boolean;
}

export async function getSuggestedQuestions(): Promise<SuggestedQuestion[]> {
  const { data } = await apiClient.get('/chat/suggested-questions');
  return data;
}

// ── Conversations ──

export async function createConversation(): Promise<string> {
  const { data } = await apiClient.post('/chat/conversations');
  return data.conversationKey;
}

export async function listConversations(): Promise<Conversation[]> {
  const { data } = await apiClient.get('/chat/conversations');
  return data;
}

export async function getConversation(id: string): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
  const { data } = await apiClient.get(`/chat/conversations/${id}`);
  return data;
}

export async function archiveConversation(id: string): Promise<void> {
  await apiClient.delete(`/chat/conversations/${id}`);
}

// ── Settings ──

export async function getChatSettings(): Promise<ChatSettings> {
  const { data } = await apiClient.get('/chat/settings');
  return data;
}

export async function updateChatSettings(
  settings: Partial<ChatSettings> & { changeNote?: string },
): Promise<ChatSettings> {
  const { data } = await apiClient.put('/chat/settings', settings);
  return data;
}

export interface SettingsHistoryEntry {
  HistoryKey: string;
  Version: number;
  SystemPrompt: string;
  EnabledTools: string;
  Model: string;
  MaxTokensPerMessage: number;
  Effort: string | null;
  ChangeNote: string | null;
  ChangedBy: string;
  ChangedAt: string;
}

// ── Connection diagnostics (admin) ──

export interface AiDiagnosticsResult {
  keyConfigured: boolean;
  probe: 'ok' | 'not_run' | 'auth_error' | 'model_not_found' | 'rate_limited' | 'other';
  model: string;
  detail?: string;
}

/** Admin self-test: is the server's AI key present + does a minimal request work? */
export async function testAiConnection(): Promise<AiDiagnosticsResult> {
  const { data } = await apiClient.post('/chat/diagnostics');
  return data;
}

export interface ToolInfo {
  name: string;
  description: string;
  category?: string;
}

export async function getChatSettingsHistory(): Promise<SettingsHistoryEntry[]> {
  const { data } = await apiClient.get('/chat/settings/history');
  return data;
}

export async function rollbackChatSettings(version: number): Promise<ChatSettings> {
  const { data } = await apiClient.post(`/chat/settings/rollback/${version}`);
  return data;
}

export async function getAvailableTools(): Promise<ToolInfo[]> {
  const { data } = await apiClient.get('/chat/tools');
  return data;
}

// ── Streaming message ──

export async function sendMessageStream(
  conversationId: string,
  message: string,
  onEvent: (event: SSEEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ message }),
    signal,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Stream failed' }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const event: SSEEvent = JSON.parse(line.slice(6));
          onEvent(event);
        } catch { /* skip malformed */ }
      }
    }
  }
}
